import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';
import { LibraryFolder, LibraryFile, LibraryFaq } from '../types/library';
import { libraryApi } from '../services/api/libraryApi';

import PageHeader from '../components/common/PageHeader';
import FolderTreeView from '../components/library/FolderTreeView';
import FileListTable from '../components/library/FileListTable';
import FolderDialog from '../components/library/FolderDialog';
import FileUploadDialog from '../components/library/FileUploadDialog';
import FaqSection from '../components/library/FaqSection';
import FaqDialog from '../components/library/FaqDialog';

const LibraryPage: React.FC = () => {
  const { user } = useAuthStore();
  const isAdmin = (user?.accountType ?? 0) >= AccountType.CarbonCareAdmin;

  const [loading, setLoading] = useState(true);
  const [folders, setFolders] = useState<LibraryFolder[]>([]);
  const [files, setFiles] = useState<LibraryFile[]>([]);
  const [faqs, setFaqs] = useState<LibraryFaq[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<LibraryFolder | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  // Dialog states
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState<LibraryFolder | null>(null);
  const [parentIdForNewFolder, setParentIdForNewFolder] = useState<number | undefined>();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [faqDialogOpen, setFaqDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<LibraryFaq | null>(null);

  const loadFolders = useCallback(async () => {
    try {
      const tree = await libraryApi.getFolderTree();
      setFolders(tree);
    } catch (err) {
      console.error('Failed to load folders:', err);
    }
  }, []);

  const loadFaqs = useCallback(async () => {
    try {
      const data = await libraryApi.getFaqs();
      setFaqs(data);
    } catch (err) {
      console.error('Failed to load FAQs:', err);
    }
  }, []);

  const loadFiles = useCallback(async (folderId: number) => {
    try {
      const data = await libraryApi.getFilesByFolder(folderId);
      setFiles(data);
    } catch (err) {
      console.error('Failed to load files:', err);
    }
  }, []);

  useEffect(() => {
    Promise.all([loadFolders(), loadFaqs()]).finally(() => setLoading(false));
  }, [loadFolders, loadFaqs]);

  useEffect(() => {
    if (selectedFolder) {
      loadFiles(selectedFolder.id);
    } else {
      setFiles([]);
    }
  }, [selectedFolder, loadFiles]);

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Folder handlers
  const handleCreateFolder = (parentId?: number) => {
    setEditingFolder(null);
    setParentIdForNewFolder(parentId);
    setFolderDialogOpen(true);
  };

  const handleEditFolder = (folder: LibraryFolder) => {
    setEditingFolder(folder);
    setParentIdForNewFolder(undefined);
    setFolderDialogOpen(true);
  };

  const handleDeleteFolder = async (folder: LibraryFolder) => {
    if (!globalThis.confirm(`Delete folder "${folder.name}"? Files inside will become inaccessible.`)) return;
    try {
      await libraryApi.deleteFolder(folder.id);
      showSnackbar('Folder deleted');
      if (selectedFolder?.id === folder.id) setSelectedFolder(null);
      await loadFolders();
    } catch (err) {
      console.error('Failed to delete folder:', err);
      showSnackbar('Failed to delete folder', 'error');
    }
  };

  const handleFolderSubmit = async (data: any) => {
    try {
      if (editingFolder) {
        await libraryApi.updateFolder(editingFolder.id, data);
        showSnackbar('Folder updated');
      } else {
        await libraryApi.createFolder(data);
        showSnackbar('Folder created');
      }
      await loadFolders();
    } catch (err) {
      console.error('Failed to save folder:', err);
      showSnackbar('Failed to save folder', 'error');
    }
  };

  // File handlers
  const handleDownloadFile = async (file: LibraryFile) => {
    try {
      const result = await libraryApi.downloadFile(file.id);
      if (result.url) {
        globalThis.open(result.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to download file:', err);
      showSnackbar('Failed to download file', 'error');
    }
  };

  const handleUploadFile = async (formData: FormData) => {
    await libraryApi.uploadFile(formData);
    showSnackbar('File uploaded');
    if (selectedFolder) await loadFiles(selectedFolder.id);
  };

  const handleDeleteFile = async (file: LibraryFile) => {
    if (!globalThis.confirm(`Delete file "${file.title}"?`)) return;
    try {
      await libraryApi.deleteFile(file.id);
      showSnackbar('File deleted');
      if (selectedFolder) await loadFiles(selectedFolder.id);
    } catch (err) {
      console.error('Failed to delete file:', err);
      showSnackbar('Failed to delete file', 'error');
    }
  };

  // FAQ handlers
  const handleFaqSubmit = async (data: any) => {
    try {
      if (editingFaq) {
        await libraryApi.updateFaq(editingFaq.id, data);
        showSnackbar('FAQ updated');
      } else {
        await libraryApi.createFaq(data);
        showSnackbar('FAQ created');
      }
      await loadFaqs();
    } catch (err) {
      console.error('Failed to save FAQ:', err);
      showSnackbar('Failed to save FAQ', 'error');
    }
  };

  const handleDeleteFaq = async (faq: LibraryFaq) => {
    if (!globalThis.confirm('Delete this FAQ?')) return;
    try {
      await libraryApi.deleteFaq(faq.id);
      showSnackbar('FAQ deleted');
      await loadFaqs();
    } catch (err) {
      console.error('Failed to delete FAQ:', err);
      showSnackbar('Failed to delete FAQ', 'error');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader description="Browse and download reference documents and templates." />

      <Box sx={{ display: 'flex', gap: 3, mb: 4, minHeight: 400 }}>
        {/* Folder tree - left panel */}
        <Paper variant="outlined" sx={{ width: 300, flexShrink: 0, overflow: 'auto' }}>
          <FolderTreeView
            folders={folders}
            selectedFolderId={selectedFolder?.id ?? null}
            onSelectFolder={setSelectedFolder}
            onCreateFolder={isAdmin ? handleCreateFolder : undefined}
            onEditFolder={isAdmin ? handleEditFolder : undefined}
            onDeleteFolder={isAdmin ? handleDeleteFolder : undefined}
            isAdmin={isAdmin}
          />
        </Paper>

        {/* File list - right panel */}
        <Paper variant="outlined" sx={{ flexGrow: 1, p: 2 }}>
          {selectedFolder ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography variant="h6">{selectedFolder.name}</Typography>
                  {selectedFolder.description && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedFolder.description}
                    </Typography>
                  )}
                </Box>
                {isAdmin && (
                  <Button
                    variant="contained"
                    startIcon={<UploadIcon />}
                    onClick={() => setUploadDialogOpen(true)}
                  >
                    Upload File
                  </Button>
                )}
              </Box>
              <FileListTable
                files={files}
                onDownload={handleDownloadFile}
                onDelete={isAdmin ? handleDeleteFile : undefined}
                isAdmin={isAdmin}
              />
            </>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary">Select a folder to view its files</Typography>
            </Box>
          )}
        </Paper>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* FAQ section */}
      <FaqSection
        faqs={faqs}
        onAdd={isAdmin ? () => { setEditingFaq(null); setFaqDialogOpen(true); } : undefined}
        onEdit={isAdmin ? (faq) => { setEditingFaq(faq); setFaqDialogOpen(true); } : undefined}
        onDelete={isAdmin ? handleDeleteFaq : undefined}
        isAdmin={isAdmin}
      />

      {/* Dialogs */}
      <FolderDialog
        open={folderDialogOpen}
        onClose={() => setFolderDialogOpen(false)}
        onSubmit={handleFolderSubmit}
        folder={editingFolder}
        parentId={parentIdForNewFolder}
      />

      {selectedFolder && (
        <FileUploadDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          onSubmit={handleUploadFile}
          folderId={selectedFolder.id}
        />
      )}

      <FaqDialog
        open={faqDialogOpen}
        onClose={() => setFaqDialogOpen(false)}
        onSubmit={handleFaqSubmit}
        faq={editingFaq}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LibraryPage;
